export type FunnelPayload = Record<string, unknown>;

export const SCORING_WEIGHT_LABELS: Record<string, string> = {
  magnitude: 'Magnitud de la operación',
  authority: 'Autoridad del contacto',
  currentTooling: 'Herramientas actuales',
  painMatch: 'Necesidad declarada',
  priorityCountry: 'País prioritario',
  channel: 'Canal de origen',
  urgency: 'Urgencia',
};

export const INTAKE_RESERVED_TARGETS = ['$phone', '$email', '$vertical'] as const;

export function cloneFunnelPayload(payload: FunnelPayload): FunnelPayload {
  return structuredClone(payload);
}

export function pruneOrphanVerticalRoutes(payload: FunnelPayload): FunnelPayload {
  const verticals = asRecord(payload.verticals);
  const routing = asRecord(payload.routing);
  if (!verticals || !routing) {
    return payload;
  }
  const routes = asRecord(routing.verticalRoutes);
  if (!routes) {
    return payload;
  }
  const verticalKeys = new Set(Object.keys(verticals));
  const nextRoutes: Record<string, string> = {};
  for (const [routeKey, verticalKey] of Object.entries(routes)) {
    if (typeof verticalKey === 'string' && verticalKeys.has(verticalKey)) {
      nextRoutes[routeKey] = verticalKey;
    }
  }
  return {
    ...payload,
    routing: {
      ...routing,
      verticalRoutes: nextRoutes,
    },
  };
}

export function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

export function listVerticalKeys(payload: FunnelPayload): string[] {
  const verticals = asRecord(payload.verticals);
  return verticals ? Object.keys(verticals) : [];
}

export function getVertical(payload: FunnelPayload, key: string): Record<string, unknown> | null {
  const verticals = asRecord(payload.verticals);
  if (!verticals) {
    return null;
  }
  return asRecord(verticals[key]);
}

export function getVerticalLabel(payload: FunnelPayload, key: string): string {
  const vertical = getVertical(payload, key);
  return typeof vertical?.label === 'string' ? vertical.label : key;
}

export function listSlotKeys(payload: FunnelPayload, verticalKey: string | 'common'): string[] {
  if (verticalKey === 'common') {
    const slots = payload.commonSlots;
    if (!Array.isArray(slots)) {
      return [];
    }
    return slots
      .map(slot => asRecord(slot)?.key)
      .filter((key): key is string => typeof key === 'string');
  }
  const vertical = getVertical(payload, verticalKey);
  const slots = vertical?.slots;
  if (!Array.isArray(slots)) {
    return [];
  }
  return slots
    .map(slot => asRecord(slot)?.key)
    .filter((key): key is string => typeof key === 'string');
}

export function listAllSlotTargets(payload: FunnelPayload): string[] {
  const keys = new Set<string>([...INTAKE_RESERVED_TARGETS]);
  for (const key of listSlotKeys(payload, 'common')) {
    keys.add(key);
  }
  for (const verticalKey of listVerticalKeys(payload)) {
    for (const key of listSlotKeys(payload, verticalKey)) {
      keys.add(key);
    }
  }
  return [...keys];
}

export function getSlotQuestion(
  payload: FunnelPayload,
  scope: string | 'common',
  slotKey: string,
): string {
  const slots =
    scope === 'common'
      ? payload.commonSlots
      : getVertical(payload, scope)?.slots;
  if (!Array.isArray(slots)) {
    return '';
  }
  for (const entry of slots) {
    const slot = asRecord(entry);
    if (slot?.key === slotKey && typeof slot.question === 'string') {
      return slot.question;
    }
  }
  return '';
}

export function setSlotQuestion(
  payload: FunnelPayload,
  scope: string | 'common',
  slotKey: string,
  question: string,
): void {
  const slots =
    scope === 'common'
      ? payload.commonSlots
      : getVertical(payload, scope)?.slots;
  if (!Array.isArray(slots)) {
    return;
  }
  for (const entry of slots) {
    const slot = asRecord(entry);
    if (slot?.key === slotKey) {
      slot.question = question.slice(0, 500);
      return;
    }
  }
}

export function getScoringCut(payload: FunnelPayload): number {
  const scoring = asRecord(payload.scoring);
  return typeof scoring?.cut === 'number' ? scoring.cut : 60;
}

export function setScoringCut(payload: FunnelPayload, cut: number): void {
  if (!Number.isFinite(cut)) {
    return;
  }
  const scoring = asRecord(payload.scoring) ?? {};
  scoring.cut = Math.min(100, Math.max(0, cut));
  payload.scoring = scoring;
}

export function getScoringWeights(payload: FunnelPayload): Record<string, number> {
  const scoring = asRecord(payload.scoring);
  const weights = asRecord(scoring?.weights);
  if (!weights) {
    return {};
  }
  const result: Record<string, number> = {};
  for (const [key, value] of Object.entries(weights)) {
    if (typeof value === 'number') {
      result[key] = value;
    }
  }
  return result;
}

export function setScoringWeight(payload: FunnelPayload, key: string, value: number): void {
  if (!Number.isFinite(value)) {
    return;
  }
  const scoring = asRecord(payload.scoring) ?? {};
  const weights = asRecord(scoring.weights) ?? {};
  weights[key] = Math.min(100, Math.max(0, value));
  scoring.weights = weights;
  payload.scoring = scoring;
}

export function sumScoringWeights(payload: FunnelPayload): number {
  return Object.values(getScoringWeights(payload)).reduce((acc, value) => acc + value, 0);
}

export function getPlaybookString(payload: FunnelPayload, path: string[]): string {
  let current: unknown = payload.playbook;
  for (const segment of path) {
    const record = asRecord(current);
    if (!record) {
      return '';
    }
    current = record[segment];
  }
  return typeof current === 'string' ? current : '';
}

export function setPlaybookString(payload: FunnelPayload, path: string[], value: string): void {
  const playbook = asRecord(payload.playbook) ?? {};
  let current: Record<string, unknown> = playbook;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index];
    const next = asRecord(current[segment]) ?? {};
    current[segment] = next;
    current = next;
  }
  current[path[path.length - 1]] = value.slice(0, 2000);
  payload.playbook = playbook;
}

export function getKeywordsLines(payload: FunnelPayload, path: string[]): string {
  let current: unknown = payload;
  for (const segment of path) {
    const record = asRecord(current);
    if (!record) {
      return '';
    }
    current = record[segment];
  }
  if (!Array.isArray(current)) {
    return '';
  }
  return current.filter(item => typeof item === 'string').join('\n');
}

export function setKeywordsLines(payload: FunnelPayload, path: string[], raw: string): void {
  const keywords = raw
    .split('\n')
    .map(line => line.trim().slice(0, 64))
    .filter(Boolean)
    .slice(0, 64);
  let current: Record<string, unknown> = payload;
  for (let index = 0; index < path.length - 1; index += 1) {
    const segment = path[index];
    const next = asRecord(current[segment]) ?? {};
    current[segment] = next;
    current = next;
  }
  current[path[path.length - 1]] = keywords;
}

export function getIntakeMatchWindow(payload: FunnelPayload): number {
  const intake = asRecord(payload.intake);
  return typeof intake?.matchWindowDays === 'number' ? intake.matchWindowDays : 7;
}

export function setIntakeMatchWindow(payload: FunnelPayload, days: number): void {
  const intake = asRecord(payload.intake) ?? {};
  intake.matchWindowDays = Math.min(90, Math.max(1, Math.round(days)));
  payload.intake = intake;
}

export type IntakeMappingRow = { formField: string; target: string };

export function getIntakeMappings(payload: FunnelPayload, source: 'META_FORM' | 'WEB_FORM'): IntakeMappingRow[] {
  const intake = asRecord(payload.intake);
  const fieldMappings = asRecord(intake?.fieldMappings);
  const sourceMap = asRecord(fieldMappings?.[source]);
  if (!sourceMap) {
    return [];
  }
  return Object.entries(sourceMap).map(([formField, target]) => ({
    formField,
    target: typeof target === 'string' ? target : '',
  }));
}

export function setIntakeMappings(
  payload: FunnelPayload,
  source: 'META_FORM' | 'WEB_FORM',
  rows: IntakeMappingRow[],
): void {
  const intake = asRecord(payload.intake) ?? {};
  const fieldMappings = asRecord(intake.fieldMappings) ?? {};
  const next: Record<string, string> = {};
  for (const row of rows.slice(0, 50)) {
    const formField = row.formField.trim().slice(0, 128);
    const target = row.target.trim().slice(0, 64);
    if (formField && target) {
      next[formField] = target;
    }
  }
  fieldMappings[source] = next;
  intake.fieldMappings = fieldMappings;
  payload.intake = intake;
}

export function getVerticalThresholdMin(payload: FunnelPayload, verticalKey: string): number | null {
  const vertical = getVertical(payload, verticalKey);
  const threshold = asRecord(vertical?.threshold);
  if (!threshold || threshold.min === null) {
    return null;
  }
  return typeof threshold.min === 'number' ? threshold.min : null;
}

export function setVerticalThresholdMin(
  payload: FunnelPayload,
  verticalKey: string,
  min: number | null,
): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  const threshold = vertical ? asRecord(vertical.threshold) ?? {} : null;
  if (!vertical || !threshold || !verticals) {
    return;
  }
  threshold.min = min;
  vertical.threshold = threshold;
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function getVerticalCountries(payload: FunnelPayload, verticalKey: string): string {
  const vertical = getVertical(payload, verticalKey);
  const gate = asRecord(vertical?.countryGate);
  const allowed = gate?.allowed;
  if (!Array.isArray(allowed)) {
    return '';
  }
  return allowed.filter(item => typeof item === 'string').join(', ');
}

export function setVerticalCountries(payload: FunnelPayload, verticalKey: string, raw: string): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  const gate = vertical ? asRecord(vertical.countryGate) ?? {} : null;
  if (!vertical || !gate || !verticals) {
    return;
  }
  const allowed = raw
    .split(/[,\s]+/)
    .map(code => code.trim().toUpperCase().slice(0, 2))
    .filter(code => code.length === 2);
  gate.allowed = allowed.length > 0 ? allowed : ['AR'];
  vertical.countryGate = gate;
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function getVerticalCountryMode(payload: FunnelPayload, verticalKey: string): 'CUT' | 'WARN' {
  const vertical = getVertical(payload, verticalKey);
  const gate = asRecord(vertical?.countryGate);
  return gate?.mode === 'CUT' ? 'CUT' : 'WARN';
}

export function setVerticalCountryMode(
  payload: FunnelPayload,
  verticalKey: string,
  mode: 'CUT' | 'WARN',
): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  const gate = vertical ? asRecord(vertical.countryGate) ?? {} : null;
  if (!vertical || !gate || !verticals) {
    return;
  }
  gate.mode = mode;
  vertical.countryGate = gate;
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function setVerticalLabel(payload: FunnelPayload, verticalKey: string, label: string): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  if (!vertical || !verticals) {
    return;
  }
  vertical.label = label.slice(0, 120);
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export type SlotScope = string | 'common';

/**
 * Etapa del dato en el contrato v2 del embudo. Sólo `must` cambia el comportamiento:
 * el bot lo pregunta y sin él no se decide la derivación. `should` y `nice` se guardan
 * si el prospecto los cuenta, pero nunca se preguntan ni bloquean.
 */
export type SlotStage = 'must' | 'should' | 'nice';

export const SLOT_STAGES: readonly SlotStage[] = ['must', 'should', 'nice'];

export const SLOT_STAGE_LABELS: Record<SlotStage, string> = {
  must: 'Imprescindible: el bot lo pregunta',
  should: 'Útil: se guarda si lo cuenta',
  nice: 'Opcional: se guarda si lo cuenta',
};

function parseSlotStage(value: unknown): SlotStage {
  return value === 'must' || value === 'should' || value === 'nice' ? value : 'nice';
}

export type SlotSnapshot = {
  key: string;
  type: 'number' | 'enum' | 'text';
  stage: SlotStage;
  priority: number;
  unit?: string;
  acceptsRange?: boolean;
  values?: string[];
  question?: string;
};

export type RoutingOwnerRow = {
  userId: number;
  verticals: string[];
  countries: string[];
  weight?: number;
};

export type InstitutionalMetricKey = 'yearsExperience' | 'projects' | 'countries';

const MAX_SLOTS_PER_SCOPE = 40;
const MAX_ROUTING_OWNERS = 50;
const MAX_ENUM_VALUES = 40;
const MAX_NEVER_LINES = 64;

function normalizeEntityKey(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

function getSlotsArray(payload: FunnelPayload, scope: SlotScope): unknown[] | null {
  if (scope === 'common') {
    return Array.isArray(payload.commonSlots) ? payload.commonSlots : null;
  }
  const vertical = getVertical(payload, scope);
  return Array.isArray(vertical?.slots) ? vertical.slots : null;
}

function ensureSlotsArray(payload: FunnelPayload, scope: SlotScope): unknown[] {
  if (scope === 'common') {
    if (!Array.isArray(payload.commonSlots)) {
      payload.commonSlots = [];
    }
    return payload.commonSlots as unknown[];
  }
  const verticals = asRecord(payload.verticals) ?? {};
  const vertical = asRecord(verticals[scope]) ?? {};
  if (!Array.isArray(vertical.slots)) {
    vertical.slots = [];
  }
  verticals[scope] = vertical;
  payload.verticals = verticals;
  return vertical.slots as unknown[];
}

function slotFromRecord(entry: unknown): SlotSnapshot | null {
  const slot = asRecord(entry);
  if (!slot || typeof slot.key !== 'string') {
    return null;
  }
  const rawType = slot.type;
  const type: SlotSnapshot['type'] =
    rawType === 'number' || rawType === 'enum' || rawType === 'text' ? rawType : 'text';
  const values = Array.isArray(slot.values)
    ? slot.values.filter((item): item is string => typeof item === 'string').slice(0, MAX_ENUM_VALUES)
    : undefined;
  return {
    key: slot.key.slice(0, 64),
    type,
    stage: parseSlotStage(slot.stage),
    priority:
      typeof slot.priority === 'number' && Number.isFinite(slot.priority)
        ? Math.min(999, Math.max(0, Math.round(slot.priority)))
        : 0,
    unit: typeof slot.unit === 'string' ? slot.unit.slice(0, 32) : undefined,
    acceptsRange: slot.acceptsRange === true,
    values,
    question: typeof slot.question === 'string' ? slot.question.slice(0, 500) : undefined,
  };
}

function parseStringList(value: unknown, maxItems: number, itemMaxLen: number): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === 'string')
    .map(item => item.trim().slice(0, itemMaxLen))
    .filter(Boolean)
    .slice(0, maxItems);
}

export function getSlots(payload: FunnelPayload, scope: SlotScope): SlotSnapshot[] {
  const slots = getSlotsArray(payload, scope);
  if (!slots) {
    return [];
  }
  const result: SlotSnapshot[] = [];
  for (const entry of slots) {
    const snapshot = slotFromRecord(entry);
    if (snapshot) {
      result.push(snapshot);
    }
  }
  return result;
}

function defaultNewSlot(scopeKeys: string[]): Record<string, unknown> {
  let index = 1;
  let key = 'new_slot';
  while (scopeKeys.includes(key) && index < 100) {
    key = `new_slot_${index}`;
    index += 1;
  }
  return {
    key,
    type: 'text',
    stage: 'nice',
    priority: 50,
    question: '',
  };
}

export function addSlot(payload: FunnelPayload, scope: SlotScope, slot?: Partial<SlotSnapshot>): void {
  const slots = ensureSlotsArray(payload, scope);
  if (slots.length >= MAX_SLOTS_PER_SCOPE) {
    return;
  }
  const existingKeys = getSlots(payload, scope).map(row => row.key);
  const base = defaultNewSlot(existingKeys);
  if (slot?.key) {
    base.key = normalizeEntityKey(slot.key) || String(base.key);
  }
  if (slot?.type === 'number' || slot?.type === 'enum' || slot?.type === 'text') {
    base.type = slot.type;
  }
  if (typeof slot?.priority === 'number' && Number.isFinite(slot.priority)) {
    base.priority = Math.min(999, Math.max(0, Math.round(slot.priority)));
  }
  if (slot?.type === 'enum') {
    base.values = slot.values?.length ? slot.values.slice(0, MAX_ENUM_VALUES) : ['opcion_1'];
  }
  slots.push(base);
}

export function removeSlot(payload: FunnelPayload, scope: SlotScope, key: string): void {
  const slots = getSlotsArray(payload, scope);
  if (!slots) {
    return;
  }
  const next = slots.filter(entry => asRecord(entry)?.key !== key);
  if (scope === 'common') {
    payload.commonSlots = next;
    return;
  }
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[scope]) : null;
  if (!vertical || !verticals) {
    return;
  }
  vertical.slots = next;
  verticals[scope] = vertical;
  payload.verticals = verticals;
}

export function updateSlotField(
  payload: FunnelPayload,
  scope: SlotScope,
  key: string,
  field: keyof SlotSnapshot,
  value: string | number | boolean | string[],
): void {
  const slots = getSlotsArray(payload, scope);
  if (!slots) {
    return;
  }
  for (const entry of slots) {
    const slot = asRecord(entry);
    if (slot?.key !== key) {
      continue;
    }
    if (field === 'key' && typeof value === 'string') {
      const nextKey = normalizeEntityKey(value);
      if (nextKey) {
        slot.key = nextKey;
      }
      return;
    }
    if (field === 'type' && (value === 'number' || value === 'enum' || value === 'text')) {
      slot.type = value;
      if (value === 'enum' && (!Array.isArray(slot.values) || slot.values.length === 0)) {
        slot.values = ['opcion_1'];
      }
      if (value !== 'number') {
        delete slot.unit;
        delete slot.acceptsRange;
      }
      return;
    }
    if (field === 'stage' && (value === 'must' || value === 'should' || value === 'nice')) {
      slot.stage = value;
      return;
    }
    if (field === 'priority' && typeof value === 'number' && Number.isFinite(value)) {
      slot.priority = Math.min(999, Math.max(0, Math.round(value)));
      return;
    }
    if (field === 'unit' && typeof value === 'string') {
      slot.unit = value.slice(0, 32);
      return;
    }
    if (field === 'acceptsRange' && typeof value === 'boolean') {
      slot.acceptsRange = value;
      return;
    }
    if (field === 'question' && typeof value === 'string') {
      slot.question = value.slice(0, 500);
      return;
    }
    if (field === 'values' && Array.isArray(value)) {
      slot.values = value
        .filter((item): item is string => typeof item === 'string')
        .map(item => item.trim().slice(0, 64))
        .filter(Boolean)
        .slice(0, MAX_ENUM_VALUES);
      return;
    }
  }
}

export function getSlotEnumValues(payload: FunnelPayload, scope: SlotScope, key: string): string[] {
  const slots = getSlots(payload, scope);
  const slot = slots.find(row => row.key === key);
  return slot?.values ?? [];
}

export function setSlotEnumValues(
  payload: FunnelPayload,
  scope: SlotScope,
  key: string,
  values: string[],
): void {
  const normalized = values
    .map(item => item.trim().slice(0, 64))
    .filter(Boolean)
    .slice(0, MAX_ENUM_VALUES);
  updateSlotField(payload, scope, key, 'values', normalized);
}

function defaultVerticalDef(label: string): Record<string, unknown> {
  const slotKey = 'magnitud';
  return {
    label: label.slice(0, 120),
    countryGate: { allowed: ['AR'], mode: 'WARN' },
    primaryMagnitude: slotKey,
    threshold: { slot: slotKey, min: null },
    // El contrato v2 exige al menos un dato en el camino rápido (pedido de reunión).
    fastPathSlots: [slotKey],
    slots: [
      {
        key: slotKey,
        type: 'number',
        stage: 'must',
        priority: 10,
        question: '',
      },
    ],
    painKeywords: [],
  };
}

export function addVertical(payload: FunnelPayload, key: string, label: string): boolean {
  const normalizedKey = normalizeEntityKey(key);
  if (!normalizedKey) {
    return false;
  }
  const verticals = asRecord(payload.verticals) ?? {};
  if (verticals[normalizedKey]) {
    return false;
  }
  verticals[normalizedKey] = defaultVerticalDef(label || normalizedKey);
  payload.verticals = verticals;
  return true;
}

export function removeVertical(payload: FunnelPayload, key: string): void {
  const verticals = asRecord(payload.verticals);
  if (!verticals) {
    return;
  }
  delete verticals[key];
  payload.verticals = verticals;
}

export function renameVerticalKey(payload: FunnelPayload, from: string, to: string): boolean {
  const normalizedTo = normalizeEntityKey(to);
  if (!normalizedTo || normalizedTo === from) {
    return false;
  }
  const verticals = asRecord(payload.verticals);
  if (!verticals || !verticals[from] || verticals[normalizedTo]) {
    return false;
  }
  verticals[normalizedTo] = verticals[from];
  delete verticals[from];
  payload.verticals = verticals;
  return true;
}

export function getVerticalPrimaryMagnitude(payload: FunnelPayload, verticalKey: string): string {
  const vertical = getVertical(payload, verticalKey);
  return typeof vertical?.primaryMagnitude === 'string' ? vertical.primaryMagnitude : '';
}

export function setVerticalPrimaryMagnitude(
  payload: FunnelPayload,
  verticalKey: string,
  slotKey: string,
): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  if (!vertical || !verticals) {
    return;
  }
  vertical.primaryMagnitude = slotKey.slice(0, 64);
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function getVerticalThresholdSlot(payload: FunnelPayload, verticalKey: string): string {
  const vertical = getVertical(payload, verticalKey);
  const threshold = asRecord(vertical?.threshold);
  return typeof threshold?.slot === 'string' ? threshold.slot : '';
}

export function setVerticalThresholdSlot(
  payload: FunnelPayload,
  verticalKey: string,
  slotKey: string,
): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  const threshold = vertical ? asRecord(vertical.threshold) ?? {} : null;
  if (!vertical || !threshold || !verticals) {
    return;
  }
  threshold.slot = slotKey.slice(0, 64);
  vertical.threshold = threshold;
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function getVerticalFastPathSlots(payload: FunnelPayload, verticalKey: string): string[] {
  const vertical = getVertical(payload, verticalKey);
  if (!Array.isArray(vertical?.fastPathSlots)) {
    return [];
  }
  return vertical.fastPathSlots.filter((item): item is string => typeof item === 'string').slice(0, 40);
}

export function setVerticalFastPathSlots(
  payload: FunnelPayload,
  verticalKey: string,
  slotKeys: string[],
): void {
  const verticals = asRecord(payload.verticals);
  const vertical = verticals ? asRecord(verticals[verticalKey]) : null;
  if (!vertical || !verticals) {
    return;
  }
  vertical.fastPathSlots = slotKeys
    .map(key => key.trim().slice(0, 64))
    .filter(Boolean)
    .slice(0, 40);
  verticals[verticalKey] = vertical;
  payload.verticals = verticals;
}

export function getInstitutionalMetric(
  payload: FunnelPayload,
  metric: InstitutionalMetricKey,
): { value: number; unconfirmed: boolean } {
  const institutional = asRecord(payload.institutional);
  const field = asRecord(institutional?.[metric]);
  const value = typeof field?.value === 'number' && Number.isFinite(field.value) ? field.value : 0;
  return { value, unconfirmed: field?.unconfirmed === true };
}

export function setInstitutionalValue(
  payload: FunnelPayload,
  metric: InstitutionalMetricKey,
  value: number,
): void {
  if (!Number.isFinite(value)) {
    return;
  }
  const institutional = asRecord(payload.institutional) ?? {};
  const field = asRecord(institutional[metric]) ?? { value: 0, unconfirmed: true };
  field.value = value;
  institutional[metric] = field;
  payload.institutional = institutional;
}

export function setInstitutionalUnconfirmed(
  payload: FunnelPayload,
  metric: InstitutionalMetricKey,
  unconfirmed: boolean,
): void {
  const institutional = asRecord(payload.institutional) ?? {};
  const field = asRecord(institutional[metric]) ?? { value: 0, unconfirmed: true };
  field.unconfirmed = unconfirmed;
  institutional[metric] = field;
  payload.institutional = institutional;
}

export function getGuardrailsNeverLines(payload: FunnelPayload): string {
  return getKeywordsLines(payload, ['playbook', 'guardrails', 'never']);
}

export function setGuardrailsNeverLines(payload: FunnelPayload, raw: string): void {
  const lines = raw
    .split('\n')
    .map(line => line.trim().slice(0, 200))
    .filter(Boolean)
    .slice(0, MAX_NEVER_LINES);
  let current: Record<string, unknown> = payload;
  for (let index = 0; index < 2; index += 1) {
    const segment = ['playbook', 'guardrails'][index];
    const next = asRecord(current[segment]) ?? {};
    current[segment] = next;
    current = next;
  }
  current.never = lines;
}

function ensureWebAnonymous(payload: FunnelPayload): Record<string, unknown> {
  const existing = asRecord(payload.webAnonymous);
  if (existing) {
    return existing;
  }
  const section = { attend: false, identitySlotKeys: [] as string[] };
  payload.webAnonymous = section;
  return section;
}

export function getWebAnonymousAttend(payload: FunnelPayload): boolean {
  const section = asRecord(payload.webAnonymous);
  return section?.attend === true;
}

export function setWebAnonymousAttend(payload: FunnelPayload, attend: boolean): void {
  const section = ensureWebAnonymous(payload);
  section.attend = attend;
  payload.webAnonymous = section;
}

export function getWebAnonymousIdentityKeys(payload: FunnelPayload): string[] {
  const section = asRecord(payload.webAnonymous);
  if (!Array.isArray(section?.identitySlotKeys)) {
    return [];
  }
  return section.identitySlotKeys
    .filter((item): item is string => typeof item === 'string')
    .map(key => key.slice(0, 64))
    .slice(0, 10);
}

export function setWebAnonymousIdentityKeys(payload: FunnelPayload, keys: string[]): void {
  const section = ensureWebAnonymous(payload);
  section.identitySlotKeys = keys
    .map(key => key.trim().slice(0, 64))
    .filter(Boolean)
    .slice(0, 10);
  payload.webAnonymous = section;
}

const ROUTING_STRATEGIES = ['ROUND_ROBIN', 'BY_VERTICAL', 'BY_COUNTRY'] as const;
export type RoutingStrategy = (typeof ROUTING_STRATEGIES)[number];

export function getRoutingStrategy(payload: FunnelPayload): string {
  const routing = asRecord(payload.routing);
  const strategy = typeof routing?.strategy === 'string' ? routing.strategy : 'ROUND_ROBIN';
  return strategy.slice(0, 64);
}

export function setRoutingStrategy(payload: FunnelPayload, strategy: string): void {
  const routing = asRecord(payload.routing) ?? {};
  const normalized = strategy.slice(0, 64);
  routing.strategy = ROUTING_STRATEGIES.includes(normalized as RoutingStrategy)
    ? normalized
    : 'ROUND_ROBIN';
  if (!Array.isArray(routing.owners)) {
    routing.owners = [];
  }
  payload.routing = routing;
}

export function getRoutingOwners(payload: FunnelPayload): RoutingOwnerRow[] {
  const routing = asRecord(payload.routing);
  if (!Array.isArray(routing?.owners)) {
    return [];
  }
  const rows: RoutingOwnerRow[] = [];
  for (const entry of routing.owners.slice(0, MAX_ROUTING_OWNERS)) {
    const record = asRecord(entry);
    if (!record) {
      continue;
    }
    const userIdRaw = record.userId;
    const userId =
      typeof userIdRaw === 'number'
        ? userIdRaw
        : typeof userIdRaw === 'string'
          ? Number(userIdRaw)
          : NaN;
    if (!Number.isFinite(userId)) {
      continue;
    }
    rows.push({
      userId: Math.round(userId),
      verticals: parseStringList(record.verticals, 20, 64),
      countries: parseStringList(record.countries, 20, 2).map(code => code.toUpperCase()),
      weight:
        typeof record.weight === 'number' && Number.isFinite(record.weight)
          ? record.weight
          : undefined,
    });
  }
  return rows;
}

export function setRoutingOwners(payload: FunnelPayload, rows: RoutingOwnerRow[]): void {
  const routing = asRecord(payload.routing) ?? {};
  const owners: Record<string, unknown>[] = [];
  for (const row of rows.slice(0, MAX_ROUTING_OWNERS)) {
    if (!Number.isFinite(row.userId)) {
      continue;
    }
    const entry: Record<string, unknown> = {
      userId: Math.round(row.userId),
    };
    if (row.verticals.length > 0) {
      entry.verticals = row.verticals.map(v => v.trim().slice(0, 64)).filter(Boolean).slice(0, 20);
    }
    if (row.countries.length > 0) {
      entry.countries = row.countries
        .map(code => code.trim().toUpperCase().slice(0, 2))
        .filter(code => code.length === 2)
        .slice(0, 20);
    }
    if (typeof row.weight === 'number' && Number.isFinite(row.weight)) {
      entry.weight = row.weight;
    }
    owners.push(entry);
  }
  routing.owners = owners;
  if (typeof routing.strategy !== 'string') {
    routing.strategy = 'ROUND_ROBIN';
  }
  payload.routing = routing;
}

export function getFunnelConfigValidationWarnings(payload: FunnelPayload): string[] {
  const warnings: string[] = [];
  const commonKeys = new Set(listSlotKeys(payload, 'common'));

  for (const verticalKey of listVerticalKeys(payload)) {
    const slotKeys = new Set(listSlotKeys(payload, verticalKey));
    const primary = getVerticalPrimaryMagnitude(payload, verticalKey);
    if (primary && !slotKeys.has(primary)) {
      warnings.push(
        `Vertical "${verticalKey}": la magnitud principal "${primary}" no está entre los slots de la vertical.`,
      );
    }
    const thresholdSlot = getVerticalThresholdSlot(payload, verticalKey);
    if (thresholdSlot && !slotKeys.has(thresholdSlot)) {
      warnings.push(
        `Vertical "${verticalKey}": el slot de umbral "${thresholdSlot}" no existe en la vertical.`,
      );
    }
    for (const fastPathSlot of getVerticalFastPathSlots(payload, verticalKey)) {
      if (!slotKeys.has(fastPathSlot) && !commonKeys.has(fastPathSlot)) {
        warnings.push(
          `Vertical "${verticalKey}": fast path "${fastPathSlot}" no está en la vertical ni en slots comunes.`,
        );
      }
    }
    const verticalSlots = getSlots(payload, verticalKey);
    if (!verticalSlots.some(slot => slot.stage === 'must')) {
      warnings.push(
        `Vertical "${verticalKey}": necesita al menos un dato imprescindible; sin eso el bot no tiene qué preguntar.`,
      );
    }
    if (getVerticalFastPathSlots(payload, verticalKey).length === 0) {
      warnings.push(
        `Vertical "${verticalKey}": el fast path necesita al menos un dato (lo mínimo que se pide a quien acepta una reunión).`,
      );
    }
    for (const slot of verticalSlots) {
      if (slot.stage === 'must' && !slot.question?.trim()) {
        warnings.push(
          `Vertical "${verticalKey}": el dato imprescindible "${slot.key}" no tiene pregunta; el bot no sabría cómo pedirlo.`,
        );
      }
    }
    for (const slot of verticalSlots) {
      if (slot.type === 'enum' && (!slot.values || slot.values.length === 0)) {
        warnings.push(`Vertical "${verticalKey}": el slot enum "${slot.key}" requiere valores.`);
      }
    }
  }

  for (const slot of getSlots(payload, 'common')) {
    if (slot.type === 'enum' && (!slot.values || slot.values.length === 0)) {
      warnings.push(`Slot común enum "${slot.key}" requiere al menos un valor.`);
    }
  }

  for (const identityKey of getWebAnonymousIdentityKeys(payload)) {
    if (!commonKeys.has(identityKey)) {
      warnings.push(
        `Web anónima: la clave "${identityKey}" no existe en slots comunes.`,
      );
    }
  }

  if (listVerticalKeys(payload).length < 1) {
    warnings.push('Debe existir al menos una vertical.');
  }

  return warnings;
}

export function isValidVerticalKeyFormat(key: string): boolean {
  const trimmed = key.trim().slice(0, 64);
  return trimmed.length > 0 && /^[a-z0-9_]+$/.test(trimmed);
}
